'use client';

import { useEffect, type MutableRefObject } from 'react';
import { toast } from 'sonner';
import type { Id } from '@/convex/_generated/dataModel';
import type { LiveVoiceSession } from '@/lib/live-voice-session';
import type { QueuedRealtimeInternalMessage } from '@/lib/realtime-voice-agent';
import { buildRealtimeStatusPrompt } from '@/lib/realtime-voice-agent';
import {
  LIVE_VOICE_BILLING_FAILED_PREFIX,
  shouldProcessLiveVoiceStatus,
} from '@/lib/live-voice-status';
import { useRealtimeVoiceFailureStatus } from './use-realtime-voice-failure-status';
import type { RealtimeVoiceCallControllerArgs } from './realtime-voice-call-controller-args';

type Controllers = {
  responses: {
    hasConversationActivityRef: MutableRefObject<boolean>;
    queueOrSendInternalMessage: (
      message: QueuedRealtimeInternalMessage
    ) => boolean;
  };
  ui: {
    setStatus: (status: 'error') => void;
    setErrorText: (message: string) => void;
  };
};

export function useRealtimeVoiceStatusUpdates(args: {
  open: boolean;
  orgThreadId?: Id<'orgThreads'> | null;
  realtimeStatus: RealtimeVoiceCallControllerArgs['realtimeStatus'];
  connectionVersion: number;
  leaseVersion: number;
  activeLeaseSessionId: string | null;
  sessionRef: MutableRefObject<LiveVoiceSession | null>;
  controllersRef: MutableRefObject<Controllers>;
  leaseSessionIdRef: MutableRefObject<string | null>;
  callLeaseSessionIdRef: MutableRefObject<string | null>;
  callOpenedAtRef: MutableRefObject<number | null>;
  lastStatusEventIdRef: MutableRefObject<string | null>;
  onEnd: () => void;
  billingError: string;
}) {
  const {
    open,
    orgThreadId,
    realtimeStatus,
    connectionVersion,
    leaseVersion,
    activeLeaseSessionId,
    sessionRef,
    controllersRef,
    leaseSessionIdRef,
    callLeaseSessionIdRef,
    callOpenedAtRef,
    lastStatusEventIdRef,
    onEnd,
    billingError,
  } = args;
  const { pendingFailure, acknowledgeFailure } = useRealtimeVoiceFailureStatus({
    open,
    orgThreadId,
    sessionId: activeLeaseSessionId,
  });
  useEffect(() => {
    void connectionVersion;
    if (!open) return;
    const event = realtimeStatus?.turnId.startsWith(
      LIVE_VOICE_BILLING_FAILED_PREFIX
    )
      ? realtimeStatus
      : (pendingFailure ?? realtimeStatus);
    if (!event) return;
    if (
      !shouldProcessLiveVoiceStatus({
        event,
        lastSeenEventId: lastStatusEventIdRef.current,
        activeLeaseSessionId: leaseSessionIdRef.current,
        callLeaseSessionId: callLeaseSessionIdRef.current,
        callOpenedAt: callOpenedAtRef.current,
      })
    ) {
      return;
    }
    if (event.turnId.startsWith(LIVE_VOICE_BILLING_FAILED_PREFIX)) {
      const controllers = controllersRef.current;
      lastStatusEventIdRef.current = event.turnId;
      const message =
        event.text === 'LIVE_VOICE_BILLING_FAILED' ? billingError : event.text;
      toast.error(message);
      controllers.ui.setStatus('error');
      controllers.ui.setErrorText(message);
      onEnd();
      return;
    }
    if (!sessionRef.current) return;
    const controllers = controllersRef.current;
    if (
      !controllers.responses.hasConversationActivityRef.current &&
      !event.turnId.startsWith('realtime-status-org_failure-')
    ) {
      lastStatusEventIdRef.current = event.turnId;
      return;
    }
    const accepted = controllers.responses.queueOrSendInternalMessage({
      key: `status:${event.turnId}`,
      message: buildRealtimeStatusPrompt(event.text),
      ticket: {
        kind: 'internal_status',
        eventId: event.turnId,
        reason: 'realtime_status_event',
      },
    });
    if (!accepted) return;
    if (
      pendingFailure?.turnId === event.turnId &&
      event.turnId.startsWith('realtime-status-org_failure-')
    ) {
      // Live append commands have no positive delivery acknowledgment. Show
      // these facts before retiring the receipt, even if narration is rejected.
      try {
        toast.error(event.text);
      } catch {
        return;
      }
    }
    lastStatusEventIdRef.current = event.turnId;
  }, [
    open,
    realtimeStatus,
    pendingFailure,
    connectionVersion,
    onEnd,
    leaseVersion,
    billingError,
    callLeaseSessionIdRef,
    callOpenedAtRef,
    controllersRef,
    lastStatusEventIdRef,
    leaseSessionIdRef,
    sessionRef,
  ]);

  // Narration and receipt acknowledgment have separate retry state.
  useEffect(() => {
    if (
      !open ||
      !pendingFailure ||
      pendingFailure.sessionId !== activeLeaseSessionId ||
      lastStatusEventIdRef.current !== pendingFailure.turnId
    )
      return;
    let canceled = false;
    let delay = 1_000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const acknowledge = () => {
      void acknowledgeFailure(pendingFailure).catch(() => {
        if (canceled) return;
        timer = setTimeout(acknowledge, delay);
        delay = Math.min(delay * 2, 30_000);
      });
    };
    acknowledge();
    return () => {
      canceled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    open,
    pendingFailure,
    activeLeaseSessionId,
    acknowledgeFailure,
    connectionVersion,
    lastStatusEventIdRef,
  ]);
}
